import { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useAuthStore } from '../../../store/authStore';
import { chatService } from '../../../services/chatService';
import { adminService } from '../../../services/adminService';
import { notificationService } from '../../../services/notificationService';

const FloatingContainer = styled.div`
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 1000;
`;

const ChatButton = styled.button`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: #007bff;
  color: white;
  border: none;
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.4);
  cursor: pointer;
  font-size: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
  position: relative;

  &:hover {
    background-color: #0056b3;
    transform: scale(1.1);
  }
`;

const UnreadBadge = styled.div`
  position: absolute;
  top: -5px;
  right: -5px;
  background-color: #dc3545;
  color: white;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
`;

const ChatWindow = styled.div`
  position: fixed;
  bottom: 100px;
  right: 24px;
  width: 380px;
  height: 600px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 1001;

  @media (max-width: 768px) {
    width: calc(100vw - 48px);
    height: calc(100vh - 150px);
  }
`;

const ChatHeader = styled.div`
  background-color: #007bff;
  color: white;
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HeaderTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    opacity: 0.8;
  }
`;

const ChatRoomList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px;
`;

const ChatRoomItem = styled.div`
  padding: 12px;
  border-bottom: 1px solid #e9ecef;
  cursor: pointer;
  transition: background-color 0.2s;
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:hover {
    background-color: #f8f9fa;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const ChatRoomInfo = styled.div`
  flex: 1;
`;

const ChatRoomName = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: #333;
  margin-bottom: 4px;
`;

const ChatRoomLastMessage = styled.div`
  font-size: 13px;
  color: #666;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ChatRoomBadge = styled.div`
  background-color: #dc3545;
  color: white;
  border-radius: 12px;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: bold;
  min-width: 20px;
  text-align: center;
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  background-color: #f8f9fa;
`;

const MessageBubble = styled.div`
  max-width: 70%;
  margin-bottom: 12px;
  padding: 10px 14px;
  border-radius: 12px;
  font-size: 14px;
  line-height: 1.4;
  word-wrap: break-word;

  ${props => props.$isMine ? `
    background-color: #007bff;
    color: white;
    margin-left: auto;
    border-bottom-right-radius: 4px;
  ` : `
    background-color: white;
    color: #333;
    border-bottom-left-radius: 4px;
  `}
`;

const MessageSender = styled.div`
  font-size: 11px;
  margin-bottom: 4px;
  opacity: 0.8;
`;

const MessageTime = styled.div`
  font-size: 11px;
  margin-top: 4px;
  opacity: 0.7;
`;

const InputContainer = styled.div`
  padding: 12px;
  border-top: 1px solid #e9ecef;
  display: flex;
  gap: 8px;
  background: white;
`;

const Input = styled.input`
  flex: 1;
  padding: 10px 12px;
  border: 1px solid #dee2e6;
  border-radius: 20px;
  font-size: 14px;
  outline: none;

  &:focus {
    border-color: #007bff;
  }
`;

const SendButton = styled.button`
  padding: 10px 20px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #0056b3;
  }

  &:disabled {
    background-color: #6c757d;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #999;
  font-size: 14px;
  padding: 24px;
  text-align: center;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  padding: 0;
  margin-right: 12px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    opacity: 0.8;
  }
`;

const StartChatButton = styled.button`
  margin: 16px;
  padding: 12px 24px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: #0056b3;
  }
`;

const StudentListItem = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid #e9ecef;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f8f9fa;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const StudentName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
`;

const StudentInfo = styled.div`
  font-size: 12px;
  color: #666;
`;

const FloatingChat = () => {
  const { userProfile } = useAuthStore();
  const [isOpen, setIsOpen] = useState(false);
  const [chatRooms, setChatRooms] = useState([]);
  const [selectedChatRoom, setSelectedChatRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [totalUnread, setTotalUnread] = useState(0);
  const [users, setUsers] = useState([]);
  const [showStudentList, setShowStudentList] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  // 알림 초기화
  useEffect(() => {
    const initNotifications = async () => {
      if (userProfile) {
        const hasPermission = await notificationService.initialize();
        if (hasPermission) {
          await notificationService.getToken(userProfile.id);

          // 포그라운드 메시지 리스너
          notificationService.onMessageReceived((data) => {
            // 새 메시지 알림 시 채팅방 열기
            if (data?.chatRoomId) {
              const room = chatRooms.find(r => r.id === data.chatRoomId);
              if (room) {
                setSelectedChatRoom(room);
                setIsOpen(true);
              }
            }
          });
        }
      }
    };

    initNotifications();
  }, [userProfile]);

  // 사용자 목록 가져오기
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const usersData = await adminService.getAllUsers();
        setUsers(usersData);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };

    if (userProfile) {
      fetchUsers();
    }
  }, [userProfile]);

  // 채팅방 목록 실시간 구독
  useEffect(() => {
    if (!userProfile) return;

    const unsubscribe = chatService.subscribeToChatRooms(
      userProfile.id,
      userProfile.type,
      (rooms) => {
        setChatRooms(rooms);
      }
    );

    return () => unsubscribe();
  }, [userProfile]);

  // 읽지 않은 메시지 수 실시간 구독
  useEffect(() => {
    if (!userProfile) return;

    const unsubscribe = chatService.subscribeToUnreadCount(
      userProfile.id,
      userProfile.type,
      (count) => {
        setTotalUnread(count);
      }
    );

    return () => unsubscribe();
  }, [userProfile]);

  // 선택된 채팅방의 메시지 실시간 구독
  useEffect(() => {
    if (!selectedChatRoom) return;

    const unsubscribe = chatService.subscribeToMessages(
      selectedChatRoom.id,
      (msgs) => {
        setMessages(msgs);
        // 메시지를 읽음으로 표시
        if (isOpen && userProfile) {
          chatService.markMessagesAsRead(selectedChatRoom.id, userProfile.id);
        }
      }
    );

    return () => unsubscribe();
  }, [selectedChatRoom, isOpen, userProfile]);

  // 메시지 스크롤
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleToggleChat = () => {
    setIsOpen(!isOpen);
    if (!isOpen && selectedChatRoom && userProfile) {
      // 채팅창을 열 때 메시지를 읽음으로 표시
      chatService.markMessagesAsRead(selectedChatRoom.id, userProfile.id);
    }
  };

  const handleSelectChatRoom = (room) => {
    setSelectedChatRoom(room);
    if (userProfile) {
      chatService.markMessagesAsRead(room.id, userProfile.id);
    }
  };

  const handleSendMessage = async () => {
    if (!messageInput.trim() || !selectedChatRoom || !userProfile) return;

    try {
      await chatService.sendMessage({
        chatRoomId: selectedChatRoom.id,
        senderId: userProfile.id,
        senderType: userProfile.type,
        senderName: userProfile.name,
        message: messageInput.trim(),
        mediaType: 'text'
      });
      setMessageInput('');
    } catch (error) {
      console.error('Error sending message:', error);
      alert('메시지 전송에 실패했습니다.');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedChatRoom || !userProfile) return;

    try {
      setUploadingFile(true);
      setUploadProgress(0);

      // 파일 업로드
      const result = await chatService.uploadFile(
        file,
        selectedChatRoom.id,
        (progress) => {
          setUploadProgress(progress);
        }
      );

      // 미디어 타입 결정
      const mediaType = file.type.startsWith('image/') ? 'image' : 'video';
      const messageText = mediaType === 'image' ? '📷 사진' : '🎥 동영상';

      // 메시지 전송
      await chatService.sendMessage({
        chatRoomId: selectedChatRoom.id,
        senderId: userProfile.id,
        senderType: userProfile.type,
        senderName: userProfile.name,
        message: messageText,
        mediaType,
        mediaUrl: result.url,
        fileName: result.fileName,
        fileSize: result.fileSize
      });

      // 파일 input 초기화
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      alert(error.message || '파일 업로드에 실패했습니다.');
    } finally {
      setUploadingFile(false);
      setUploadProgress(0);
    }
  };

  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diff = now - date;

    // 1분 미만
    if (diff < 60000) {
      return '방금 전';
    }

    // 1시간 미만
    if (diff < 3600000) {
      const minutes = Math.floor(diff / 60000);
      return `${minutes}분 전`;
    }

    // 24시간 미만
    if (diff < 86400000) {
      const hours = Math.floor(diff / 3600000);
      return `${hours}시간 전`;
    }

    // 그 외
    return date.toLocaleDateString('ko-KR', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getChatRoomName = (room) => {
    if (userProfile.type === 'teacher') {
      return room.studentName || '학부모';
    } else {
      // 학부모인 경우 치료사 이름 표시
      const teacher = users.find(u => u.id === room.teacherId);
      return teacher ? `${teacher.name} 치료사` : '치료사';
    }
  };

  const handleStartNewChat = () => {
    setShowStudentList(true);
  };

  const handleSelectStudent = async (student) => {
    try {
      // 채팅방 찾기 또는 생성
      const chatRoom = await chatService.findOrCreateChatRoom(
        userProfile.id,
        student.id,
        student.studentName
      );

      setSelectedChatRoom(chatRoom);
      setShowStudentList(false);
    } catch (error) {
      console.error('Error creating chat room:', error);
      alert('채팅방 생성에 실패했습니다.');
    }
  };

  const getMyStudents = () => {
    // 치료사인 경우 배정된 학부모 목록
    if (userProfile.type === 'teacher') {
      return users.filter(u => u.type === 'mom' && u.teacher_ids && u.teacher_ids.includes(userProfile.id));
    }
    return [];
  };

  // 관리자나 학부모는 채팅 기능을 사용하지 않음
  if (!userProfile || (userProfile.type !== 'teacher' && userProfile.type !== 'mom')) {
    return null;
  }

  return (
    <FloatingContainer>
      {isOpen && (
        <ChatWindow>
          <ChatHeader>
            {(selectedChatRoom || showStudentList) && (
              <BackButton onClick={() => {
                setSelectedChatRoom(null);
                setShowStudentList(false);
              }}>
                ←
              </BackButton>
            )}
            <HeaderTitle>
              {selectedChatRoom
                ? getChatRoomName(selectedChatRoom)
                : showStudentList
                ? '학생 선택'
                : '채팅'}
            </HeaderTitle>
            <CloseButton onClick={handleToggleChat}>×</CloseButton>
          </ChatHeader>

          {showStudentList ? (
            <ChatRoomList>
              {getMyStudents().length === 0 ? (
                <EmptyState>
                  배정된 학생이 없습니다.
                </EmptyState>
              ) : (
                getMyStudents().map((student) => (
                  <StudentListItem key={student.id} onClick={() => handleSelectStudent(student)}>
                    <StudentName>{student.studentName || student.name}</StudentName>
                    <StudentInfo>학부모: {student.name}</StudentInfo>
                  </StudentListItem>
                ))
              )}
            </ChatRoomList>
          ) : !selectedChatRoom ? (
            <>
              {userProfile.type === 'teacher' && chatRooms.length === 0 && (
                <StartChatButton onClick={handleStartNewChat}>
                  + 새 채팅 시작
                </StartChatButton>
              )}
              <ChatRoomList>
                {chatRooms.length === 0 ? (
                  <EmptyState>
                    {userProfile.type === 'teacher'
                      ? '학부모와의 채팅방이 없습니다.'
                      : '치료사와의 채팅방이 없습니다.\n궁금한 점이 있으시면\n채팅을 시작해보세요.'}
                  </EmptyState>
                ) : (
                  chatRooms.map((room) => (
                    <ChatRoomItem key={room.id} onClick={() => handleSelectChatRoom(room)}>
                      <ChatRoomInfo>
                        <ChatRoomName>{getChatRoomName(room)}</ChatRoomName>
                        <ChatRoomLastMessage>
                          {room.lastMessage || '메시지가 없습니다'}
                        </ChatRoomLastMessage>
                      </ChatRoomInfo>
                      {room.unreadCount?.[userProfile.id] > 0 && (
                        <ChatRoomBadge>
                          {room.unreadCount[userProfile.id]}
                        </ChatRoomBadge>
                      )}
                    </ChatRoomItem>
                  ))
                )}
              </ChatRoomList>
              {userProfile.type === 'teacher' && chatRooms.length > 0 && (
                <StartChatButton onClick={handleStartNewChat}>
                  + 새 채팅 시작
                </StartChatButton>
              )}
            </>
          ) : (
            <>
              <MessagesContainer>
                {messages.length === 0 ? (
                  <EmptyState>
                    대화를 시작해보세요
                  </EmptyState>
                ) : (
                  messages.map((msg) => (
                    <MessageBubble
                      key={msg.id}
                      $isMine={msg.senderId === userProfile.id}
                    >
                      {msg.senderId !== userProfile.id && (
                        <MessageSender>{msg.senderName}</MessageSender>
                      )}
                      {msg.mediaType === 'image' && msg.mediaUrl ? (
                        <div>
                          <img
                            src={msg.mediaUrl}
                            alt={msg.fileName}
                            style={{
                              maxWidth: '250px',
                              maxHeight: '250px',
                              borderRadius: '8px',
                              cursor: 'pointer'
                            }}
                            onClick={() => window.open(msg.mediaUrl, '_blank')}
                          />
                          {msg.message && msg.message !== '📷 사진' && (
                            <div style={{ marginTop: '8px' }}>{msg.message}</div>
                          )}
                        </div>
                      ) : msg.mediaType === 'video' && msg.mediaUrl ? (
                        <div>
                          <video
                            src={msg.mediaUrl}
                            controls
                            style={{
                              maxWidth: '250px',
                              maxHeight: '250px',
                              borderRadius: '8px'
                            }}
                          />
                          {msg.message && msg.message !== '🎥 동영상' && (
                            <div style={{ marginTop: '8px' }}>{msg.message}</div>
                          )}
                        </div>
                      ) : (
                        <div>{msg.message}</div>
                      )}
                      <MessageTime>{formatTime(msg.createdAt)}</MessageTime>
                    </MessageBubble>
                  ))
                )}
                <div ref={messagesEndRef} />
              </MessagesContainer>

              {uploadingFile && (
                <div style={{
                  padding: '12px',
                  backgroundColor: '#f8f9fa',
                  borderTop: '1px solid #dee2e6',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '14px', marginBottom: '4px' }}>
                      업로드 중... {Math.round(uploadProgress)}%
                    </div>
                    <div style={{
                      width: '100%',
                      height: '4px',
                      backgroundColor: '#e9ecef',
                      borderRadius: '2px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        width: `${uploadProgress}%`,
                        height: '100%',
                        backgroundColor: '#007bff',
                        transition: 'width 0.3s'
                      }} />
                    </div>
                  </div>
                </div>
              )}

              <InputContainer>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,video/*"
                  style={{ display: 'none' }}
                  onChange={handleFileSelect}
                />
                <SendButton
                  onClick={handleAttachClick}
                  disabled={uploadingFile}
                  style={{
                    backgroundColor: '#6c757d',
                    marginRight: '8px'
                  }}
                  title="이미지 또는 동영상 첨부"
                >
                  📎
                </SendButton>
                <Input
                  type="text"
                  placeholder="메시지를 입력하세요..."
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={uploadingFile}
                />
                <SendButton
                  onClick={handleSendMessage}
                  disabled={!messageInput.trim() || uploadingFile}
                >
                  전송
                </SendButton>
              </InputContainer>
            </>
          )}
        </ChatWindow>
      )}

      <ChatButton onClick={handleToggleChat}>
        💬
        {totalUnread > 0 && <UnreadBadge>{totalUnread}</UnreadBadge>}
      </ChatButton>
    </FloatingContainer>
  );
};

export default FloatingChat;
